import * as React from "react";
import { useAction, useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import { FileText, Upload, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAromas, useFlavors, useOrigins, useRegions, useProducers } from "@/hooks/useReferenceData";
import { toUserFacingErrorMessage } from "@/lib/errors";
import { useAppLocale } from "@/lib/i18n";
import {
  prepareProductImageForUpload,
  PRODUCT_IMAGE_HELP_TEXT,
  PRODUCT_IMAGE_INPUT_ACCEPT,
  PRODUCT_IMAGE_TOO_LARGE_MESSAGE,
  validateProductImageFile,
} from "@/lib/product-image-upload";
import {
  prepareProductInfoSheetForUpload,
  PRODUCT_INFO_SHEET_HELP_TEXT,
  PRODUCT_INFO_SHEET_INPUT_ACCEPT,
  PRODUCT_INFO_SHEET_TOO_LARGE_MESSAGE,
  validateProductInfoSheetFile,
} from "@/lib/product-info-sheet-upload";
import { m } from "@/paraglide/messages.js";

import { normalizeTastingNotes } from "./product-tasting-notes";
import { RegionManagementDialog } from "./region-management-dialog";
import { AromaManagementDialog, FlavorManagementDialog } from "./tasting-note-management-dialog";
import { TastingNotesSelect } from "./tasting-notes-select";

interface ProductCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  duplicateFrom?: any | null;
}

const initialForm = {
  name: "",
  description: "",
  originId: "",
  regionId: "",
  producerId: "",
  varietal: "",
  millingProcess: "",
  masl: "",
  harvestCycle: "",
  acidity: "",
  bodyFeel: "",
  flavorNotes: [] as string[],
  aromaNotes: [] as string[],
  weight: "",
  score: "",
  grainPro: false,
  onTheWater: false,
  waterActivity: "",
  moisturePercentage: "",
  agtronInternal: "",
  samplesStock: "",
  lastCupDate: "",
  greenNotes: "",
  pertinentInfo: "",
  recommendedProfile: "",
};

type ProductCreateForm = typeof initialForm;

function emptyCreateForm(): ProductCreateForm {
  return {
    ...initialForm,
    flavorNotes: [],
    aromaNotes: [],
  };
}

function toCreateForm(product?: any | null): ProductCreateForm {
  if (!product) {
    return emptyCreateForm();
  }

  return {
    ...emptyCreateForm(),
    name: product.name ?? "",
    description: product.description ?? "",
    originId: product.originId ?? "",
    regionId: product.regionId ?? "",
    producerId: product.producerId ?? "",
    varietal: product.varietal ?? "",
    millingProcess: product.millingProcess ?? "",
    masl: product.masl ?? "",
    harvestCycle: product.harvestCycle ?? "",
    acidity: product.acidity ?? "",
    bodyFeel: product.bodyFeel ?? "",
    flavorNotes: normalizeTastingNotes(
      product.flavorNames ?? product.flavors?.map((flavor: any) => flavor?.name),
    ),
    aromaNotes: normalizeTastingNotes(
      product.aromaNames ?? product.aromas?.map((aroma: any) => aroma?.name),
    ),
    weight: product.weight != null ? String(product.weight) : "",
    score: product.score != null ? String(product.score) : "",
    grainPro: product.grainPro ?? false,
    onTheWater: product.onTheWater ?? false,
    waterActivity: product.waterActivity != null ? String(product.waterActivity) : "",
    moisturePercentage: product.moisturePercentage != null ? String(product.moisturePercentage) : "",
    agtronInternal: product.agtronInternal != null ? String(product.agtronInternal) : "",
    samplesStock: product.samplesStock != null ? String(product.samplesStock) : "",
    lastCupDate: product.lastCupDate ?? "",
    greenNotes: product.greenNotes ?? "",
    pertinentInfo: product.pertinentInfo ?? "",
    recommendedProfile: product.recommendedProfile ?? "",
  };
}

export function ProductCreateDialog({ open, onOpenChange, duplicateFrom }: ProductCreateDialogProps) {
  const { locale } = useAppLocale();
  const [form, setForm] = React.useState<ProductCreateForm>(() => emptyCreateForm());
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const [infoSheetDragOver, setInfoSheetDragOver] = React.useState(false);
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [infoSheetFile, setInfoSheetFile] = React.useState<File | null>(null);
  const [regionsOpen, setRegionsOpen] = React.useState(false);
  const [flavorsOpen, setFlavorsOpen] = React.useState(false);
  const [aromasOpen, setAromasOpen] = React.useState(false);
  const isDuplicate = Boolean(duplicateFrom);
  const duplicateTitle = locale === "es" ? "Duplicar producto" : "Duplicate Product";
  const duplicateDescription = locale === "es"
    ? "Prefillado desde el producto seleccionado. Nada se crea hasta que guardes; los archivos no se copian."
    : "Prefilled from the selected product. Nothing is created until you save; files are not copied.";
  const duplicateButtonLabel = locale === "es" ? "Crear duplicado" : "Create Duplicate";
  const createProduct = useMutation(api.products.createProduct);
  const updateImage = useAction(api.products.updateProductImage);
  const updateInfoSheet = useAction(api.products.updateProductInfoSheet);
  const origins = useOrigins();
  const regions = useRegions();
  const producers = useProducers();
  const flavors = useFlavors();
  const aromas = useAromas();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const infoSheetInputRef = React.useRef<HTMLInputElement>(null);

  const imagePreviewUrl = React.useMemo(
    () => (imageFile ? URL.createObjectURL(imageFile) : null),
    [imageFile],
  );

  React.useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    setForm(toCreateForm(duplicateFrom));
    setImageFile(null);
    setInfoSheetFile(null);
    setDragOver(false);
    setInfoSheetDragOver(false);
  }, [open, duplicateFrom]);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error(m.product_name_required());
      return;
    }

    const parsedWeight = Number(form.weight);
    if (!form.weight.trim() || !Number.isFinite(parsedWeight) || parsedWeight <= 0) {
      toast.error("Weight is required and must be greater than 0.");
      return;
    }

    setSaving(true);
    try {
      const createdProduct = await createProduct({
        name: form.name.trim(),
        description: form.description || undefined,
        originId: form.originId || undefined,
        regionId: form.regionId || undefined,
        producerId: form.producerId || undefined,
        varietal: form.varietal || undefined,
        millingProcess: form.millingProcess || undefined,
        masl: form.masl || undefined,
        harvestCycle: form.harvestCycle || undefined,
        acidity: form.acidity || undefined,
        bodyFeel: form.bodyFeel || undefined,
        flavorNames: normalizeTastingNotes(form.flavorNotes),
        aromaNames: normalizeTastingNotes(form.aromaNotes),
        weight: parsedWeight,
        score: form.score ? Number(form.score) : undefined,
        grainPro: form.grainPro || undefined,
        onTheWater: form.onTheWater || undefined,
        waterActivity: form.waterActivity ? Number(form.waterActivity) : undefined,
        moisturePercentage: form.moisturePercentage ? Number(form.moisturePercentage) : undefined,
        agtronInternal: form.agtronInternal ? Number(form.agtronInternal) : undefined,
        samplesStock: form.samplesStock === "" ? undefined : Number(form.samplesStock),
        lastCupDate: form.lastCupDate || undefined,
        greenNotes: form.greenNotes || undefined,
        pertinentInfo: form.pertinentInfo || undefined,
        recommendedProfile: form.recommendedProfile || undefined,
      });

      let uploadFailed = false;
      if (imageFile && createdProduct?._id) {
        setUploading(true);
        try {
          const preparedImage = await prepareProductImageForUpload(imageFile);
          await updateImage({
            productId: createdProduct._id,
            fileData: preparedImage.fileData,
            contentType: preparedImage.contentType,
            filename: preparedImage.filename,
          });
        } catch (error) {
          uploadFailed = true;
          toast.error(
            toUserFacingErrorMessage(error, "Product created, but image upload failed", {
              unauthorizedMessage: m.inventory_products_unauthorized(),
              oversizeMessage: PRODUCT_IMAGE_TOO_LARGE_MESSAGE,
            }),
          );
        } finally {
          setUploading(false);
        }
      }

      if (infoSheetFile && createdProduct?._id) {
        setUploading(true);
        try {
          const preparedInfoSheet = await prepareProductInfoSheetForUpload(infoSheetFile);
          await updateInfoSheet({
            productId: createdProduct._id,
            fileData: preparedInfoSheet.fileData,
            contentType: preparedInfoSheet.contentType,
            filename: preparedInfoSheet.filename,
          });
        } catch (error) {
          uploadFailed = true;
          toast.error(
            toUserFacingErrorMessage(error, "Product created, but info sheet upload failed", {
              unauthorizedMessage: m.inventory_products_unauthorized(),
              oversizeMessage: PRODUCT_INFO_SHEET_TOO_LARGE_MESSAGE,
            }),
          );
        } finally {
          setUploading(false);
        }
      }

      if (!uploadFailed) {
        toast.success(m.product_created());
      }
      setForm(emptyCreateForm());
      setImageFile(null);
      setInfoSheetFile(null);
      onOpenChange(false);
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(error, "Failed to create product", {
          unauthorizedMessage:
            m.inventory_products_unauthorized(),
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleImageSelect = (file: File) => {
    const validationError = validateProductImageFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setImageFile(file);
  };

  const handleInfoSheetSelect = (file: File) => {
    const validationError = validateProductInfoSheetFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setInfoSheetFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleImageSelect(file);
  };

  const handleInfoSheetDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setInfoSheetDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleInfoSheetSelect(file);
  };

  const filteredRegions = React.useMemo(() => {
    if (!regions) return [];
    if (!form.originId) return regions;
    return regions.filter((r: any) => r.originId === form.originId);
  }, [regions, form.originId]);

  const filteredProducers = React.useMemo(() => {
    if (!producers) return [];
    if (!form.originId && !form.regionId) return producers;
    return producers.filter((p: any) => {
      if (form.regionId && p.regionId !== form.regionId) return false;
      if (form.originId && p.originId !== form.originId) return false;
      return true;
    });
  }, [producers, form.originId, form.regionId]);

  const originNameMap = React.useMemo(() => {
    const map = new Map<string, string>();
    (origins ?? []).forEach((origin: any) => {
      map.set(origin._id, origin.name);
    });
    return map;
  }, [origins]);

  const regionNameMap = React.useMemo(() => {
    const map = new Map<string, string>();
    (regions ?? []).forEach((region: any) => {
      map.set(region._id, region.name);
    });
    return map;
  }, [regions]);

  const originOptions = React.useMemo(
    () => (origins ?? []).map((origin: any) => ({ value: origin._id, label: origin.name })),
    [origins],
  );

  const regionOptions = React.useMemo(
    () => filteredRegions.map((region: any) => ({
      value: region._id,
      label: region.name,
      sublabel: region.originId ? originNameMap.get(region.originId) : undefined,
      searchText: [region.name, region.originId ? originNameMap.get(region.originId) : undefined]
        .filter(Boolean)
        .join(" "),
    })),
    [filteredRegions, originNameMap],
  );

  const producerOptions = React.useMemo(
    () => filteredProducers.map((producer: any) => ({
      value: producer._id,
      label: producer.name,
      sublabel: [
        producer.regionId ? regionNameMap.get(producer.regionId) : undefined,
        producer.originId ? originNameMap.get(producer.originId) : undefined,
      ]
        .filter(Boolean)
        .join(" • ") || undefined,
      searchText: [
        producer.name,
        producer.regionId ? regionNameMap.get(producer.regionId) : undefined,
        producer.originId ? originNameMap.get(producer.originId) : undefined,
      ]
        .filter(Boolean)
        .join(" "),
    })),
    [filteredProducers, originNameMap, regionNameMap],
  );

  React.useEffect(() => {
    if (!form.regionId || filteredRegions.some((region: any) => region._id === form.regionId)) {
      return;
    }

    setForm((prev) => ({ ...prev, regionId: "" }));
  }, [filteredRegions, form.regionId]);

  React.useEffect(() => {
    if (!form.producerId || filteredProducers.some((producer: any) => producer._id === form.producerId)) {
      return;
    }

    setForm((prev) => ({ ...prev, producerId: "" }));
  }, [filteredProducers, form.producerId]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isDuplicate ? duplicateTitle : m.create_product()}</DialogTitle>
          <DialogDescription>
            {isDuplicate ? duplicateDescription : m.create_product_description()}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="basic" className="mt-2">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="basic">{m.tab_basic()}</TabsTrigger>
            <TabsTrigger value="coffee">{m.tab_coffee()}</TabsTrigger>
            <TabsTrigger value="quality">{m.tab_quality()}</TabsTrigger>
            <TabsTrigger value="notes">{m.notes()}</TabsTrigger>
            <TabsTrigger value="image">{m.image()}</TabsTrigger>
            <TabsTrigger value="info-sheet">Info sheet</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4 pt-4">
            <div className="grid gap-2">
              <Label htmlFor="cp-name">{m.name_required()}</Label>
              <Input id="cp-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={m.product_name_placeholder()} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cp-desc">{m.description()}</Label>
              <Textarea id="cp-desc" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={m.product_description_placeholder()} />
            </div>
            <div className="grid gap-2">
              <Label>{m.origin()}</Label>
              <ComboboxSelect
                options={originOptions}
                value={form.originId}
                onValueChange={(value) => set("originId", value)}
                placeholder={m.select_origin()}
                searchPlaceholder="Search origins..."
                emptyText="No origins found."
                loading={origins === undefined}
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-2">
                <Label>{m.region()}</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto px-0 text-xs"
                  onClick={() => setRegionsOpen(true)}
                >
                  Manage regions
                </Button>
              </div>
              <ComboboxSelect
                options={regionOptions}
                value={form.regionId}
                onValueChange={(value) => set("regionId", value)}
                placeholder={m.select_region()}
                searchPlaceholder="Search regions..."
                emptyText="No matching regions found. Use Manage regions to add one."
                loading={regions === undefined}
              />
            </div>
            <div className="grid gap-2">
              <Label>{m.producer()}</Label>
              <ComboboxSelect
                options={producerOptions}
                value={form.producerId}
                onValueChange={(value) => set("producerId", value)}
                placeholder={m.select_producer()}
                searchPlaceholder="Search producers..."
                emptyText="No producers found."
                loading={producers === undefined}
              />
            </div>
          </TabsContent>

          <TabsContent value="coffee" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{m.varietal()}</Label>
                <Input value={form.varietal} onChange={(e) => set("varietal", e.target.value)} placeholder={m.varietal_placeholder()} />
              </div>
              <div className="grid gap-2">
                <Label>{m.milling_process()}</Label>
                <Input value={form.millingProcess} onChange={(e) => set("millingProcess", e.target.value)} placeholder={m.milling_process_placeholder()} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{m.masl()}</Label>
                <Input value={form.masl} onChange={(e) => set("masl", e.target.value)} placeholder={m.masl_placeholder()} />
              </div>
              <div className="grid gap-2">
                <Label>{m.harvest_cycle()}</Label>
                <Input value={form.harvestCycle} onChange={(e) => set("harvestCycle", e.target.value)} placeholder={m.harvest_cycle_placeholder()} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{m.acidity()}</Label>
                <Input value={form.acidity} onChange={(e) => set("acidity", e.target.value)} placeholder={m.acidity_placeholder()} />
              </div>
              <div className="grid gap-2">
                <Label>{m.body_feel()}</Label>
                <Input value={form.bodyFeel} onChange={(e) => set("bodyFeel", e.target.value)} placeholder={m.body_feel_placeholder()} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{m.weight_lbs()}</Label>
                <Input type="number" min="0.01" step="0.01" value={form.weight} onChange={(e) => set("weight", e.target.value)} placeholder="132" />
              </div>
              <div className="grid gap-2">
                <Label>{m.score()}</Label>
                <Input type="number" value={form.score} onChange={(e) => set("score", e.target.value)} placeholder="0" />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.grainPro} onCheckedChange={(v) => set("grainPro", v)} />
                <Label>{m.grainpro()}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.onTheWater} onCheckedChange={(v) => set("onTheWater", v)} />
                <Label>{m.on_the_water()}</Label>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="quality" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{m.water_activity()}</Label>
                <Input type="number" step="0.01" value={form.waterActivity} onChange={(e) => set("waterActivity", e.target.value)} placeholder="0.00" />
              </div>
              <div className="grid gap-2">
                <Label>{m.moisture_percentage()}</Label>
                <Input type="number" step="0.1" value={form.moisturePercentage} onChange={(e) => set("moisturePercentage", e.target.value)} placeholder="0.0" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{m.agtron_internal()}</Label>
                <Input type="number" value={form.agtronInternal} onChange={(e) => set("agtronInternal", e.target.value)} placeholder="0" />
              </div>
              <div className="grid gap-2">
                <Label>{m.samples_stock()}</Label>
                <Input type="number" value={form.samplesStock} onChange={(e) => set("samplesStock", e.target.value)} placeholder="0" />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>{m.last_cup_date()}</Label>
              <DatePicker value={form.lastCupDate} onChange={(v) => set("lastCupDate", v)} placeholder={m.select_date()} />
            </div>
          </TabsContent>

          <TabsContent value="notes" className="space-y-4 pt-4">
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Aroma notes</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto px-0 text-xs"
                  onClick={() => setAromasOpen(true)}
                >
                  {locale === "es" ? "Administrar aromas" : "Manage Aromas"}
                </Button>
              </div>
              <TastingNotesSelect
                value={form.aromaNotes}
                onChange={(value) => set("aromaNotes", value)}
                options={(aromas ?? []).map((item) => item.name ?? "").filter(Boolean)}
                placeholder="Select or add aroma notes"
                searchPlaceholder="Search aromas..."
                emptyText="No aromas found."
                loading={aromas === undefined}
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Flavor notes</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto px-0 text-xs"
                  onClick={() => setFlavorsOpen(true)}
                >
                  {locale === "es" ? "Administrar sabores" : "Manage Flavors"}
                </Button>
              </div>
              <TastingNotesSelect
                value={form.flavorNotes}
                onChange={(value) => set("flavorNotes", value)}
                options={(flavors ?? []).map((item) => item.name ?? "").filter(Boolean)}
                placeholder="Select or add flavor notes"
                searchPlaceholder="Search flavors..."
                emptyText="No flavors found."
                loading={flavors === undefined}
              />
            </div>
            <div className="grid gap-2">
              <Label>{m.green_notes()}</Label>
              <Textarea value={form.greenNotes} onChange={(e) => set("greenNotes", e.target.value)} placeholder={m.green_notes_placeholder()} rows={3} />
            </div>
            <div className="grid gap-2">
              <Label>{m.pertinent_info()}</Label>
              <Textarea value={form.pertinentInfo} onChange={(e) => set("pertinentInfo", e.target.value)} placeholder={m.pertinent_info_placeholder()} rows={3} />
            </div>
            <div className="grid gap-2">
              <Label>{m.recommended_profile()}</Label>
              <Textarea value={form.recommendedProfile} onChange={(e) => set("recommendedProfile", e.target.value)} placeholder={m.recommended_profile_placeholder()} rows={3} />
            </div>
          </TabsContent>

          <TabsContent value="image" className="space-y-4 pt-4">
            {imagePreviewUrl && (
              <div className="mb-4">
                <Label className="mb-2 block">{m.image()}</Label>
                <img src={imagePreviewUrl} alt={form.name || "Selected product image"} className="h-40 w-auto rounded-md border object-cover" />
              </div>
            )}
            <label
              htmlFor="create-product-image-upload"
              className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 transition-colors ${
                dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25"
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{m.drag_drop_image_or()}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                disabled={saving || uploading}
              >
                {uploading ? m.uploading() : m.choose_file()}
              </Button>
              <input
                id="create-product-image-upload"
                ref={fileInputRef}
                type="file"
                accept={PRODUCT_IMAGE_INPUT_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageSelect(file);
                }}
              />
              <p className="text-xs text-muted-foreground">{PRODUCT_IMAGE_HELP_TEXT}</p>
            </label>
          </TabsContent>

          <TabsContent value="info-sheet" className="space-y-4 pt-4">
            {infoSheetFile && (
              <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 p-3">
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm font-medium">{infoSheetFile.name}</span>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => setInfoSheetFile(null)} disabled={saving || uploading}>
                  <X className="h-4 w-4" />
                  Remove
                </Button>
              </div>
            )}
            <label
              htmlFor="create-product-info-sheet-upload"
              className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 transition-colors ${
                infoSheetDragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25"
              }`}
              onDragOver={(e) => { e.preventDefault(); setInfoSheetDragOver(true); }}
              onDragLeave={() => setInfoSheetDragOver(false)}
              onDrop={handleInfoSheetDrop}
            >
              <FileText className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Drag and drop a PDF info sheet, or</p>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  infoSheetInputRef.current?.click();
                }}
                disabled={saving || uploading}
              >
                {uploading ? m.uploading() : m.choose_file()}
              </Button>
              <input
                id="create-product-info-sheet-upload"
                ref={infoSheetInputRef}
                type="file"
                accept={PRODUCT_INFO_SHEET_INPUT_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleInfoSheetSelect(file);
                }}
              />
              <p className="text-xs text-muted-foreground">{PRODUCT_INFO_SHEET_HELP_TEXT}</p>
            </label>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{m.cancel()}</Button>
          <Button onClick={() => void handleCreate()} disabled={saving || uploading}>
            {saving || uploading ? m.creating() : isDuplicate ? duplicateButtonLabel : m.create_product()}
          </Button>
        </DialogFooter>
      </DialogContent>
      </Dialog>
      <RegionManagementDialog open={regionsOpen} onOpenChange={setRegionsOpen} />
      <FlavorManagementDialog open={flavorsOpen} onOpenChange={setFlavorsOpen} />
      <AromaManagementDialog open={aromasOpen} onOpenChange={setAromasOpen} />
    </>
  );
}
